import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { getUserEmail } from '../auth';
import { errMessage } from '../pageUtils';
import { MapBoundary } from '../components/SiteMapView';
import { ringCentre, ringRadiusM, ringAreaHa, shiftRing, type Ring } from '../api/outlineGeometry';
import { parseSavedLocation, saveSiteLocation, type SiteBoundary } from '../api/locate';
import { searchPlaces, type PlaceResult } from '../api/placeSearch';
import { onOsMapProblem } from '../api/osMap';
import { coveredAuthorityBoundaries } from '../api/lpa';
import type { DrawOutline } from '../components/LocateMap';

const LocateMap = lazy(() => import('../components/LocateMap'));

// Someone thinking of applying has no documents to read the site from, so they mark it themselves, in two stages. First a
// pin on the site, found by postcode or street search on the open basemap. The pin sets where this case gets the detailed
// OS map (about 300 m around it: see planning-locate-service tileQuota.ts), so a postcode that covers a large rural area
// still ends up detailed where the site actually is. Then the outline of the site, drawn corner by corner on the detailed
// map: every pre-application site has one.

interface Position { lat: number; lon: number }
type Stage = 'pin' | 'draw';

const MIN_RADIUS = 10;
// The circle shown around the pin until an outline is drawn.
const DEFAULT_PIN_RADIUS = 50;
const MAX_CORNERS = 199;
// The outline screen opens here on any screen: close enough for OS's plot boundaries, which a fitted view on a phone misses.
const DRAW_ZOOM = 18;
const NO_LAYERS: string[] = [];
const noop = () => undefined;

const closeRing = (points: Ring): Ring => [...points, points[0]];

function metresBetween(a: Position, b: Position): number {
  const kx = 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot((a.lon - b.lon) * kx, (a.lat - b.lat) * 110_574);
}

export default function PreAppLocateStep({ caseId, createCase, onDone, saved, onCancel, cancelling = false, embedded = false, coveredAuthorities }: {
  /** The case being located; null when it doesn't exist yet, and createCase makes it once the pin is confirmed. */
  caseId: string | null;
  createCase?: () => Promise<string>;
  onDone: (caseId: string) => void;
  saved?: string | null;
  onCancel?: () => void;
  cancelling?: boolean;
  /** Shown inside the Assess page under its own heading, so the step's headings are one level down. */
  embedded?: boolean;
  /** The local planning authorities we cover (as named in the policy library), shaded on the map while it is zoomed out. */
  coveredAuthorities?: string[];
}) {
  // Stage 1 shows only the open basemap, which needs no case; the detailed map in stage 2 counts its tiles against one.
  const [activeCaseId, setActiveCaseId] = useState<string | null>(caseId);
  const [creating, setCreating] = useState(false);
  const Heading = embedded ? 'h2' : 'h1';
  // Correcting a saved site starts at the drawing stage, from what the applicant marked before.
  const [start] = useState(() => {
    const s = parseSavedLocation(saved);
    if (!s) return null;
    const ring = s.boundary?.ring;
    return ring && ring.length >= 4
      ? { points: ring.slice(0, -1), pin: ringCentre(ring), closed: true }
      // A site saved with only a pin (before outlines were required) opens ready for its outline to be drawn.
      : { points: [] as Ring, pin: { lat: s.lat, lon: s.lon }, closed: false };
  });
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<PlaceResult[] | null>(null);
  const [more, setMore] = useState(false);
  const [found, setFound] = useState<PlaceResult | null>(null);

  const [stage, setStage] = useState<Stage>(start ? 'draw' : 'pin');
  const [pin, setPin] = useState<Position | null>(start?.pin ?? null);
  // The pin the detailed map is centred on: set when the applicant confirms it, so a pin still being placed costs nothing.
  const [sitePin, setSitePin] = useState<Position | null>(start?.pin ?? null);
  const [points, setPoints] = useState<Ring>(start?.points ?? []);
  const [closed, setClosed] = useState(start?.closed ?? false);
  const [selected, setSelected] = useState<number | null>(null);
  const [fitToken, setFitToken] = useState(start ? 1 : 0);
  // Where the map frames when nothing it draws gives it a place: the place searched for, then the confirmed pin.
  const [view, setView] = useState<Position | null>(start?.pin ?? null);
  // Finding the site, off until asked for: the open map is usually enough there, and MasterMap tiles are paid for (some
  // buildings are missing from it, so it can be turned on as a second reference). Tracing the outline, on: the corners
  // need the plot boundaries.
  const [showOsMap, setShowOsMap] = useState(!!start);
  const [osMapProblem, setOsMapProblem] = useState<string | null>(null);
  useEffect(() => onOsMapProblem(setOsMapProblem), []);

  // Where checks can be run, shaded on the zoomed-out map. Only a guide, so a failed lookup just leaves it off.
  const [coverage, setCoverage] = useState<GeoJSON.FeatureCollection | null>(null);
  // The shading only shows while zoomed out, and its key with it.
  const [coverageShown, setCoverageShown] = useState(true);
  // Names the site from the map when it has no address (see LocateMap siteNamer).
  const siteNamer = useRef<((at: Position) => string | null) | null>(null);
  const coveredKey = (coveredAuthorities ?? []).join('|');
  useEffect(() => {
    if (!coveredAuthorities?.length) { setCoverage(null); return; }
    let cancelled = false;
    coveredAuthorityBoundaries(coveredAuthorities)
      .then(fc => { if (!cancelled) setCoverage(fc as GeoJSON.FeatureCollection); })
      .catch((e: Error) => { if (!cancelled) { setCoverage(null); console.warn('Covered authority boundaries unavailable', e); } });
    return () => { cancelled = true; };
  }, [coveredKey]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const drawing = stage === 'draw';
  const ring = drawing && closed && points.length >= 3 ? closeRing(points) : null;
  const centre = ring ? ringCentre(ring) : null;
  // What the map marks: the pin while placing it or when it is used on its own, the outline's centre once drawn.
  const position: Position | null = stage === 'pin' ? pin : centre;
  const searchRadius = ring && centre ? ringRadiusM(ring, centre, MIN_RADIUS, 2000) : DEFAULT_PIN_RADIUS;
  const ready = stage === 'draw' && ring != null;
  // How far the outline reaches from the pin: the detailed map's area grows to cover it, so a large site is drawn in detail too.
  const reachM = drawing && sitePin
    ? Math.max(0, ...points.map(([lon, lat]) => metresBetween(sitePin, { lat, lon })))
    : 0;

  // Delete or Backspace removes the selected corner of a finished outline.
  useEffect(() => {
    if (selected == null) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        removeSelected();
      }
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true); setSearchError(null); setResults(null);
    try {
      const r = await searchPlaces(query);
      setResults(r.results); setMore(r.more);
      if (r.results.length === 1) choose(r.results[0]);
    } catch (err) {
      setSearchError(errMessage(err, 'The search failed.'));
    } finally {
      setSearching(false);
    }
  }

  /** The detailed map is counted against a case, so on the pin screen turning it on starts the check (once). */
  function toggleOsMap(on: boolean) {
    setShowOsMap(on);
    if (on && stage === 'pin' && !activeCaseId && createCase) {
      createCase().then(setActiveCaseId).catch((e: Error) => setSaveError(errMessage(e, 'Your check could not be started. Please try again.')));
    }
  }

  function choose(r: PlaceResult) {
    setFound(r); setResults(null);
    setView({ lat: r.lat, lon: r.lon });
    if (!pin) setFitToken(t => t + 1);
  }

  async function confirmPin() {
    if (!pin) return;
    setSaveError(null);
    if (!activeCaseId) {
      if (!createCase) return;
      setCreating(true);
      try {
        setActiveCaseId(await createCase());
      } catch (e) {
        setSaveError(errMessage(e, 'Your check could not be started. Please try again.'));
        return;
      } finally {
        setCreating(false);
      }
    }
    setSitePin(pin); setView(pin); setStage('draw'); setShowOsMap(true);
    setFitToken(t => t + 1);
  }


  /** Back to finding the site from scratch: the outline and pin are cleared, and the search starts empty. */
  function chooseDifferentSite() {
    setStage('pin'); setPin(null); setPoints([]); setClosed(false); setSelected(null); setShowOsMap(false);
    setFound(null); setQuery(''); setResults(null); setMore(false); setSearchError(null); setSaveError(null);
  }

  function changeDrawing(next: Ring, isClosed: boolean) {
    setSaveError(null); setSelected(null);
    setPoints(next.slice(0, MAX_CORNERS));
    setClosed(isClosed && next.length >= 3);
  }

  function removeSelected() {
    if (selected == null || points.length <= 3) return;
    setPoints(points.filter((_, i) => i !== selected));
    setSelected(null);
  }

  function pick(p: Position) {
    setSaveError(null);
    if (stage === 'pin') { setPin(p); return; }
    // With the pin on its own, moving it moves the detailed map's area too (a few times at most: see tileQuota.ts).
    // Dragging the pin of a finished outline moves the whole shape.
    if (centre) setPoints(shiftRing(points, p.lon - centre.lon, p.lat - centre.lat));
  }

  async function confirm() {
    const at = centre;
    if (!at || !ready || !activeCaseId) return;
    setSaving(true); setSaveError(null);
    try {
      const boundary: SiteBoundary | undefined = ring
        ? {
          ring, areaHa: Math.round(ringAreaHa(ring) * 1000) / 1000 || 0.001, placement: 'drawn', confidence: 'High',
          evidence: [], warnings: [], userEdited: true,
        }
        : undefined;
      // A street searched for names the site; otherwise the nearest named road and town on the map do.
      const siteName = found?.siteName ?? siteNamer.current?.(at) ?? null;
      await saveSiteLocation(activeCaseId, {
        lat: at.lat, lon: at.lon, radiusM: Math.round(searchRadius),
        confidence: ring ? 'High' : 'Medium',
        source: 'user',
        evidence: [
          ring ? 'The applicant drew the site outline on the map.' : 'The applicant marked the site with a pin on the map.',
          ...(found ? [`They found the place by searching for "${found.label}".`] : []),
        ],
        // Designations are looked up when the case is assessed, after its token is spent (see CLAUDE.md).
        warnings: [], constraints: [],
        ...(siteName ? { siteName } : {}),
        userEdited: true,
        ...(boundary ? { boundary } : {}),
      }, getUserEmail() ?? undefined, { nameFromAddress: true });
      onDone(activeCaseId);
    } catch (e) {
      setSaveError(errMessage(e, 'The location could not be saved.'));
      setSaving(false);
    }
  }

  const draw: DrawOutline | null = drawing ? { points, closed, selected, onChange: changeDrawing, onSelect: setSelected } : null;
  const areaHa = ring ? ringAreaHa(ring) : null;

  const map = (
    <div className="lm-map-wrap">
      <div>
        <MapBoundary failMessage="The map could not be loaded.">
          <Suspense fallback={<div className="lm-map lm-map-off"><span className="skel" /></div>}>
            <LocateMap
              position={position} view={view} radiusM={stage === 'pin' ? 1 : DEFAULT_PIN_RADIUS} boundary={ring} fitToken={fitToken}
              onPick={pick} layers={NO_LAYERS} onLayerNote={noop} draw={draw}
              fitMaxZoom={stage === 'pin' ? 17 : 18} fitMinRadiusM={60} fitZoom={drawing ? DRAW_ZOOM : undefined} osDetail={showOsMap} osDetailCaseId={activeCaseId ?? undefined} osDetailPin={stage === 'draw' ? sitePin : null} osDetailReachM={reachM} osPreview={stage === 'pin' ? view : null} coverage={coverage} onCoverageShown={setCoverageShown} siteNamer={siteNamer}
            />
          </Suspense>
        </MapBoundary>
      </div>
    </div>
  );

  const osMapToggle = (
    <>
      <label className="lm-check">
        <input type="checkbox" checked={showOsMap} onChange={e => toggleOsMap(e.target.checked)} />
        Show the detailed Ordnance Survey map (buildings, gardens and house numbers, once zoomed in)
      </label>
      {showOsMap && osMapProblem && <div className="sx-note">{osMapProblem}</div>}
    </>
  );

  // Cancelling goes back to the start screen, cancelling the case if one has been made.
  const cancel = onCancel && (
    <button type="button" className="sx-cta sx-cta-2" disabled={cancelling || saving} onClick={onCancel}>
      {cancelling ? 'Cancelling…' : 'Cancel'}
    </button>
  );

  if (stage === 'pin') {
    return (
      <div className="lm-step">
        <div>
          <p className="sx-step-of">Step 1 of 3</p>
          <Heading className={embedded ? 'sx-h2' : 'sx-h1'}>Drop a pin on the site</Heading>
          <p className="sx-lede">Find it by postcode or street, then zoom in and click (or tap) the site to drop a pin. Next you'll draw its outline on the detailed map, which covers the area around your pin and grows with your outline.</p>
        </div>

        <form className="sx-field" onSubmit={e => void search(e)}>
          <label className="sx-label" htmlFor="lm-search">Postcode or street</label>
          <div className="lm-search">
            <input
              id="lm-search" className="sx-input" type="search" autoComplete="off" value={query} placeholder="e.g. BS19 4QX, or Larkspur Lane, Wrenford"
              onChange={e => setQuery(e.target.value)}
            />
            <button type="submit" className="sx-cta" disabled={searching || !query.trim()}>{searching ? 'Finding…' : 'Find'}</button>
          </div>
          {searchError && <div className="sx-err">{searchError}</div>}
          {results && results.length === 0 && <span className="sx-note">Nothing found. Check the postcode, or try the street name followed by a comma and the town.</span>}
          {results && results.length > 1 && (
            <ul className="lm-results" aria-label="Places found">
              {results.map((r, i) => (
                <li key={`${r.label}-${i}`}>
                  <button type="button" onClick={() => choose(r)}>{r.label}<small>{r.kind}</small></button>
                </li>
              ))}
            </ul>
          )}
          {more && <span className="sx-note">More places have this name. Add the town after a comma to narrow it down.</span>}
        </form>

        {map}
        {osMapToggle}
        {!!coverage?.features.length && coverageShown && <span className="sx-note"><span className="lm-coverage-key" aria-hidden="true" />Shaded: the local planning authorities we cover.</span>}
        <span className="sx-note">
          {pin ? 'Drag the pin, or click the map, to move it. Put it on the site itself: a postcode can cover a wide area.' : 'Zoom in and click (or tap) the site.'}
        </span>
        {saveError && <div className="sx-err">{saveError}</div>}

        <div className="lm-actions">
          <button type="button" className="sx-cta" disabled={!pin || creating} onClick={() => void confirmPin()}>{creating ? 'Getting the detailed map ready…' : 'This is my site'}</button>
          {cancel}
        </div>
      </div>
    );
  }

  return (
    <div className="lm-step">
      <div>
        <p className="sx-step-of">Step 2 of 3</p>
        <Heading className={embedded ? 'sx-h2' : 'sx-h1'}>Draw the outline</Heading>
        <p className="sx-lede">Click (or tap) each corner of the site in turn: the boundary of the land you want to build on or change (on a planning application this is the 'red line'). Your result will cover any designations on or near it, such as Green Belt, conservation areas and flood risk.</p>
      </div>

      <div className="sx-field">
        <span className="sx-label">Your site</span>
        <span className="sx-note">
          Pinned on the map.{' '}
          <button type="button" className="sx-link" disabled={saving} onClick={chooseDifferentSite}>Choose a different site</button>
        </span>
      </div>

      {map}
      {osMapToggle}

      <div className="sx-field">
          <span className="sx-note">
            {!closed
              ? points.length === 0
                ? 'Zoom in on your site and click (or tap) each corner of it in turn.'
                : points.length < 3
                  ? `Corner ${points.length} placed. Keep going around the site.`
                  : 'Click the first corner, or Finish, to close the outline.'
              : `Outline drawn${areaHa != null ? `, about ${areaHa < 1 ? `${Math.round(areaHa * 10_000).toLocaleString()} m²` : `${areaHa.toFixed(1)} ha`}` : ''}. Drag a corner to move it, drag the small dot in the middle of an edge to add a corner, or drag the pin to move the whole outline.`}
          </span>
          <div className="lm-draw-bar">
            {!closed && (
              <>
                <button type="button" className="sx-cta sx-cta-2" disabled={points.length === 0 || saving} onClick={() => changeDrawing(points.slice(0, -1), false)}>Undo last corner</button>
                <button type="button" className="sx-cta sx-cta-2" disabled={points.length < 3 || saving} onClick={() => changeDrawing(points, true)}>Finish</button>
              </>
            )}
            {closed && (
              <button type="button" className="sx-cta sx-cta-2" disabled={selected == null || points.length <= 3 || saving} onClick={removeSelected}>
                {selected == null ? 'Select a corner to remove it' : 'Remove this corner'}
              </button>
            )}
            <button type="button" className="sx-cta sx-cta-2" disabled={points.length === 0 || saving} onClick={() => changeDrawing([], false)}>Start again</button>
          </div>
      </div>

      {saveError && <div className="sx-err">{saveError}</div>}

      <div className="lm-actions">
        <button type="button" className="sx-cta" disabled={!ready || saving} onClick={() => void confirm()}>
          {saving ? 'Saving…' : 'Use this site'}
        </button>
        {cancel}
      </div>
    </div>
  );
}
